/**
 * Tiny in-memory log of recent runtime errors, attached to the "Reportar un
 * problema" form so a report arrives with what just went wrong. Nothing leaves
 * the browser until the user sends a report; no external service.
 */

export interface LoggedError {
  at: string;
  source: 'error' | 'unhandledrejection' | 'console';
  message: string;
  stack?: string;
}

const MAX_ENTRIES = 20;
const MAX_MESSAGE = 500;
const MAX_STACK = 1000;

const entries: LoggedError[] = [];
let installed = false;

function describe(value: unknown): { message: string; stack?: string } {
  if (value instanceof Error) return { message: value.message, stack: value.stack };
  if (typeof value === 'string') return { message: value };
  try {
    return { message: JSON.stringify(value) ?? String(value) };
  } catch {
    return { message: String(value) };
  }
}

export function recordError(source: LoggedError['source'], value: unknown): void {
  const { message, stack } = describe(value);
  entries.push({
    at: new Date().toISOString(),
    source,
    message: message.slice(0, MAX_MESSAGE),
    ...(stack ? { stack: stack.slice(0, MAX_STACK) } : {}),
  });
  if (entries.length > MAX_ENTRIES) entries.shift();
}

export function getRecentErrors(): LoggedError[] {
  return [...entries];
}

/** Idempotent: call once at app boot. */
export function installErrorLog(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  window.addEventListener('error', e => recordError('error', e.error ?? e.message));
  window.addEventListener('unhandledrejection', e => recordError('unhandledrejection', e.reason));
}
